import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  UncontrolledTooltip
} from 'reactstrap';
import Header from 'components/Headers/Header';
import { fetchMyFeedback } from '../../../redux/QuestionBank/FeedbackAction';

const FEEDBACK_TYPE_LABELS = {
  typo: { label: 'Typo', color: 'info', icon: 'fa-spell-check' },
  incorrect_answer: { label: 'Incorrect Answer', color: 'danger', icon: 'fa-times-circle' },
  unclear_question: { label: 'Unclear Question', color: 'warning', icon: 'fa-question-circle' },
  incorrect_explanation: { label: 'Wrong Explanation', color: 'danger', icon: 'fa-book' },
  image_issue: { label: 'Image Issue', color: 'secondary', icon: 'fa-image' },
  other: { label: 'Other', color: 'dark', icon: 'fa-comment' }
};

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'warning' },
  reviewed: { label: 'Reviewed', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  dismissed: { label: 'Dismissed', color: 'secondary' }
};

const MyFeedback = () => {
  const dispatch = useDispatch();
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const { myFeedback, myFeedbackLoading, myFeedbackError } = useSelector(
    state => state.questionBankFeedback || {}
  );
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const [copiedId, setCopiedId] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (userHandle) {
      dispatch(fetchMyFeedback(userHandle));
    }
  }, [dispatch, userHandle, activeSpecialty]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isUpdatedSinceFeedback = (item) => {
    if (!item.questionUpdatedAt || !item.createdAt) return false;
    const questionUpdated = item.questionUpdatedAt.toDate
      ? item.questionUpdatedAt.toDate()
      : new Date(item.questionUpdatedAt);
    const feedbackCreated = item.createdAt.toDate
      ? item.createdAt.toDate()
      : new Date(item.createdAt);
    return questionUpdated > feedbackCreated;
  };

  const handleCopyCode = (code, feedbackId) => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(feedbackId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const updatedItems = (myFeedback || []).filter(
    item => item.questionCode && isUpdatedSinceFeedback(item)
  );

  const handleCopyAllUpdated = () => {
    if (updatedItems.length === 0) return;
    const codes = updatedItems.map(item => item.questionCode).join(', ');
    navigator.clipboard.writeText(codes).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  };

  const feedback = myFeedback || [];

  return (
    <>
      <Header />
      <div className="mt--7 px-4">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col>
                    <h3 className="mb-0">
                      <i className="fas fa-comment-dots mr-2 text-purple"></i>
                      My Feedback
                      {feedback.length > 0 && (
                        <Badge color="primary" pill className="ml-2">{feedback.length}</Badge>
                      )}
                    </h3>
                  </Col>
                  <Col xs="auto">
                    {updatedItems.length > 0 && (
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleCopyAllUpdated}
                      >
                        {copiedAll ? (
                          <>
                            <i className="fas fa-check mr-1"></i>
                            Copied!
                          </>
                        ) : (
                          <>
                            <i className="fas fa-copy mr-1"></i>
                            Copy All Updated Codes ({updatedItems.length})
                          </>
                        )}
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {myFeedbackLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">Loading your feedback...</p>
                  </div>
                ) : myFeedbackError ? (
                  <div className="text-center py-5">
                    <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                    <h4>Error loading feedback</h4>
                    <p className="text-muted">{myFeedbackError}</p>
                  </div>
                ) : feedback.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-comment-slash fa-3x text-muted mb-3"></i>
                    <h4>You haven't submitted any feedback yet</h4>
                    <p className="text-muted">
                      When you report issues on questions during tests, they'll appear here.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-items-center table-hover" responsive>
                      <thead className="thead-light">
                        <tr>
                          <th scope="col">Question Code</th>
                          <th scope="col">Feedback Type</th>
                          <th scope="col">Description</th>
                          <th scope="col">Status</th>
                          <th scope="col">Submitted</th>
                          <th scope="col">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedback.map((item) => {
                          const typeInfo = FEEDBACK_TYPE_LABELS[item.feedbackType] || FEEDBACK_TYPE_LABELS.other;
                          const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
                          const updated = isUpdatedSinceFeedback(item);

                          return (
                            <tr key={item.feedbackId}>
                              <td>
                                <span className="font-weight-bold text-primary mr-2">
                                  {item.questionCode || item.questionId?.substring(0, 8) || 'N/A'}
                                </span>
                                {item.questionCode && (
                                  <>
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0 text-muted"
                                      id={`copy-${item.feedbackId}`}
                                      onClick={() => handleCopyCode(item.questionCode, item.feedbackId)}
                                    >
                                      <i className={`fas ${copiedId === item.feedbackId ? 'fa-check text-success' : 'fa-clipboard'}`}></i>
                                    </Button>
                                    <UncontrolledTooltip target={`copy-${item.feedbackId}`} placement="top">
                                      {copiedId === item.feedbackId ? 'Copied!' : 'Copy code'}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </td>
                              <td>
                                <Badge color={typeInfo.color}>
                                  <i className={`fas ${typeInfo.icon} mr-1`}></i>
                                  {typeInfo.label}
                                </Badge>
                              </td>
                              <td style={{ maxWidth: '300px' }}>
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{ maxWidth: '280px' }}
                                  title={item.description}
                                >
                                  {item.description || '-'}
                                </span>
                              </td>
                              <td>
                                <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                              </td>
                              <td>
                                <small className="text-muted">{formatDate(item.createdAt)}</small>
                              </td>
                              <td>
                                {updated ? (
                                  <Badge color="success">
                                    <i className="fas fa-sync-alt mr-1"></i>
                                    Updated
                                  </Badge>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default MyFeedback;
